import { useState, useRef, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { COLORS, SHADOWS } from '../../styles/UISystem';
import { useNavigation } from '../../contexts/NavigationContext';
import ConnectionDetailShell from './ConnectionDetailShell';
import { ALL_CONTACT_KEYS } from '../../utils/contactTypeConfig';
import { useSession } from '../../contexts/SessionContext';
import { useConnectionData, useConnectionContext } from '../../contexts/ConnectionStateContext';
import NameInput from '../UISystem/Forms/NameInput';
import ContactFields from '../UISystem/Forms/ContactFields';
import ConnectionAvatar from '../UISystem/ConnectionAvatar';
import GroupsSection from '../UISystem/Sections/GroupsSection';
import NicknameField from '../UISystem/Forms/NicknameField';
import useConnectionNote from '../../hooks/useConnectionNote';
import { HandWaving } from '@phosphor-icons/react/dist/ssr/HandWaving';
import { saveContactToPhone } from '../../utils/contactUtils';
import VenueSelectorMessage from '../Location/VenueSelectorMessage';
import LocationOverlay from '../Overlay/LocationOverlay';
import GroupSelector from '../UISystem/GroupSelector';
import useGroups from '../../hooks/useGroups';
import { saveCustomVenue } from '../../services/location/providers/radarProvider';
import { logConnectionLocation } from '../../services/analytics/eventLogger';
import { ButtonRounded } from '../UISystem/Buttons';
import AddCircleButton from '../UISystem/AddCircleButton';
import { DownloadSimple } from '@phosphor-icons/react/dist/ssr/DownloadSimple';
import { DETAIL_TOKENS } from '../../styles/UISystem';

/**
 * No ContactIconRow on this page (user ruling 2026-08-18), deliberately unlike
 * the manual frame. Every row here is the viewer's own and editable, and
 * ContactField already renders the SMS bubble on a phone row and opens the
 * dialer from its icon — so the row would be a second copy of the same
 * affordances, and a manual contact often has no contact fields at all, where
 * ContactIconRow renders an empty 16px gap rather than nothing. Same reasoning
 * that took the row off the Anon page: the contact rows carry the channels.
 */

/**
 * ManualAddDetailPage - Create/Edit a manually added contact
 *
 * This page allows Profile users to manually add people they meet
 * without going through the QR code exchange flow.
 *
 * Features:
 * - Name input (required)
 * - Note input
 * - ContactFields component for adding contacts
 * - Auto-captured location/time
 * - Tag selector (bottom sheet)
 */

const ManualAddDetailPage = () => {
  const { goBack, screenParams } = useNavigation();
  const { sessionChatRoomId } = useSession();
  const connectionData = useConnectionData(sessionChatRoomId);
  const {
    updateManualContactData,
    updateConnectionMetadata,
    updateContactSavedStatus,
    updateNickname,
  } = useConnectionContext();
  // ONE note per connection (2026-08-18 Detail frames) — nickname is the
  // survivor, the legacy notes column drains into it on first open.
  const noteText = useConnectionNote(sessionChatRoomId, connectionData, true);
  // Page-owned add-contact trigger: the frames place the + below Save.
  const addContactRef = useRef(null);
  const {
    groups,
    resolveConnectionViewId,
    getGroupsForConnection,
    actions: groupActions,
  } = useGroups();

  // Form state
  const [name, setName] = useState('');
  const [contactData, setContactData] = useState({}); // Contact fields for ContactFields component
  const [showGroupSelector, setShowGroupSelector] = useState(false);
  const [showLocationOverlay, setShowLocationOverlay] = useState(false);

  // Refs
  const nameInputRef = useRef(null);

  const initializedRef = useRef(false);

  // Load existing data if editing — only on initial load
  // Guarded so tag/location overlay saves don't reset unsaved edits
  useEffect(() => {
    if (connectionData && !initializedRef.current) {
      initializedRef.current = true;
      const manualData = connectionData.manualContactData;
      const loadedName = manualData?.name || '';
      const contacts = {};

      if (manualData) {
        for (const field of ALL_CONTACT_KEYS) {
          if (manualData[field]) {
            contacts[field] = manualData[field];
          }
        }
      }

      setName(loadedName);
      setContactData(contacts);
    }
  }, [connectionData]);

  // Focus name input on mount if new contact
  // Keyboard is already open from hidden input tap trick in ProfileConnectionsListPage,
  // so we just need to transfer focus quickly
  useEffect(() => {
    if (screenParams?.isNewContact && nameInputRef.current) {
      // Short delay to ensure DOM is painted, then transfer focus
      requestAnimationFrame(() => {
        nameInputRef.current?.focus();
        console.log('[ManualAddDetailPage] Focused name input for new contact');
      });
    }
  }, [screenParams?.isNewContact]);

  // Save data when navigating away
  const handleSave = async () => {
    if (!name.trim()) {
      console.log('[ManualAddDetailPage] Name is required, not saving');
      return false;
    }

    console.log('[ManualAddDetailPage] Saving manual contact data');

    // Build sharedFields from contactData
    const sharedFields = Object.keys(contactData).filter((key) => contactData[key]);

    // Build manual contact data
    const manualContactData = {
      name: name.trim(),
      ...contactData,
      sharedFields,
      isManualContact: true,
    };

    // Save manual contact data
    await updateManualContactData(sessionChatRoomId, manualContactData);

    // Groups are saved immediately when changed via GroupSelector, not here

    // Ensure metadata exists (location was captured on + button click)
    if (!connectionData?.connectionMetadata) {
      await updateConnectionMetadata(sessionChatRoomId, {
        createdAt: new Date().toISOString(),
        timestamp: new Date().toISOString(),
      });
    }

    console.log('[ManualAddDetailPage] Manual contact saved successfully');
    return true;
  };

  const handleBack = async () => {
    await handleSave();
    goBack('profile-connections-list');
  };

  // Swipe-back is disabled while an overlay is open (shell owns useSwipeBack)
  const swipeEnabled = !(showGroupSelector || showLocationOverlay);

  // Handle adding contact to phone
  const handleAddToContacts = async () => {
    // Save first to ensure data is current
    const saved = await handleSave();
    if (!saved) {
      console.log('[ManualAddDetailPage] Cannot add to contacts - name required');
      return;
    }

    console.log('[ManualAddDetailPage] Adding manual contact to phone');

    // Build sharedFields from contactData
    const sharedFields = Object.keys(contactData).filter((key) => contactData[key]);

    // Build userData from form state
    const userData = {
      name: name.trim(),
      ...contactData,
      sharedFields,
    };

    // handleSave above already persisted the note to app storage; the vCard
    // carries the nickname + met-line only (Company rule)
    await saveContactToPhone({
      userData,
      chatRoomId: sessionChatRoomId,
      connectionMetadata: connectionData?.connectionMetadata,
      nickname: connectionData?.nickname || '',
      updateContactSavedStatus,
    });
  };

  // Handle contact field change from ContactFields
  const handleContactChange = (patch) => {
    setContactData((prev) => ({
      ...prev,
      ...patch,
    }));
  };

  // Handle venue change from VenueSelectorMessage
  const handleVenueChange = async (selectedVenue) => {
    const metadata = connectionData?.connectionMetadata;
    const address = metadata?.locationData?.venueInfo?.address || {};
    const coords = metadata?.locationData?.coords || {};

    // Save custom venue to localStorage for future lookups at this location
    if (selectedVenue?.isCustom && selectedVenue?.name && coords.latitude && coords.longitude) {
      saveCustomVenue(selectedVenue.name, coords.latitude, coords.longitude);
    }

    const updatedMetadata = {
      ...metadata,
      locationData: {
        ...metadata?.locationData,
        venueInfo: {
          ...metadata?.locationData?.venueInfo,
          venue: selectedVenue,
        },
      },
    };

    // Update the location string
    let locationString = '';
    if (selectedVenue?.name) {
      const addressParts = [];
      if (address.streetNumber && address.street) {
        addressParts.push(`${address.streetNumber} ${address.street}`);
      } else if (address.street) {
        addressParts.push(address.street);
      }
      if (address.cityAbbr || address.city) {
        addressParts.push(address.cityAbbr || address.city);
      }
      locationString = `${selectedVenue.name}${addressParts.length > 0 ? ', ' + addressParts.join(', ') : ''}`;
    } else {
      const addressParts = [];
      if (address.streetNumber && address.street) {
        addressParts.push(`${address.streetNumber} ${address.street}`);
      } else if (address.street) {
        addressParts.push(address.street);
      }
      if (address.neighborhood) {
        addressParts.push(address.neighborhood);
      }
      if (address.cityAbbr || address.city) {
        addressParts.push(address.cityAbbr || address.city);
      }
      locationString = addressParts.join(', ');
    }
    updatedMetadata.location = locationString;

    await updateConnectionMetadata(sessionChatRoomId, updatedMetadata);

    // Log analytics for manual venue confirmation
    logConnectionLocation(sessionChatRoomId, updatedMetadata.locationData, 'manual');
  };

  return (
    <>
      <ConnectionDetailShell onBack={handleBack} swipeEnabled={swipeEnabled}>
        {/* Profile Content Block */}
        <div
          style={{
            paddingTop: '5px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            paddingLeft: '24px',
            paddingRight: '24px',
          }}
        >
          {/* Profile Photo Placeholder — manual contact, no ring */}
          <ConnectionAvatar
            name={name}
            isRoyaleUser={false}
            placeholderBg="#E5E5EA"
            initialColor={COLORS.MEDIUM_GRAY}
            fallbackIcon={<HandWaving size={40} color="#8E8E93" weight="fill" />}
          />

          {/* Name Input */}
          <NameInput
            ref={nameInputRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            focusStyle="dynamic"
          />
        </div>

        {/* Main content area — single flex gap drives the spacing between sections */}
        <div
          style={{
            flex: 1,
            padding: '20px 24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px',
          }}
        >
          {/* GROUPS — colored chips + add button (opens GroupSelector) */}
          <GroupsSection
            groups={getGroupsForConnection(sessionChatRoomId)}
            onEdit={() => setShowGroupSelector(true)}
          />

          {/* NOTE — the one note field (Note + Nickname merged, 2026-08-18):
              viewer-private, DB-first on connection_views.nickname. A note the
              Quick Add flow seeded on the legacy column drains in on first open. */}
          <div style={{ width: '100%', maxWidth: DETAIL_TOKENS.SECTION_MAX_WIDTH }}>
            <NicknameField
              value={noteText}
              onSave={(text) => updateNickname(sessionChatRoomId, text)}
            />
          </div>

          {/* WE MET — venue card (tap to edit), from connectionMetadata */}
          {connectionData?.connectionMetadata && (
            <div style={{ width: '100%', maxWidth: DETAIL_TOKENS.SECTION_MAX_WIDTH }}>
              <div
                onClick={() => setShowLocationOverlay(true)}
                style={{
                  cursor: 'pointer',
                  borderRadius: '16px',
                  boxShadow: SHADOWS.TIER1,
                  overflow: 'hidden',
                }}
              >
                <VenueSelectorMessage
                  connectionMetadata={connectionData.connectionMetadata}
                  isEditable={false}
                  showAddPlaceInvite={true}
                  backgroundColor="rgba(255, 255, 255, 0.95)"
                  showDateTime={true}
                  maxWidth={340}
                  align="left"
                />
              </div>
            </div>
          )}

          {/* CONTACTS — the contact's info; a manual contact has no peer, so
              every row is the viewer's own and stays editable */}
          <div style={{ width: '100%', maxWidth: DETAIL_TOKENS.SECTION_MAX_WIDTH }}>
            <ContactFields
              userData={contactData}
              disabled={false}
              showLinks={true}
              compact={true}
              onChange={handleContactChange}
              starterFields={[]}
              // The + sits below Save on the frames — the page places it.
              hideAddButton={true}
              addRef={addContactRef}
            />
          </div>

          {/* Save to contacts — vCard download, shown once a contact is entered,
              with the add affordance under it. Black until tapped, then white. */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            {Object.keys(contactData).some((key) => contactData[key]) && (
              <ButtonRounded
                text="Save to contacts"
                icon={DownloadSimple}
                onClick={handleAddToContacts}
                size="compact"
                variant={connectionData?.hasAddedToContacts ? 'secondary' : 'black'}
                style={{ width: DETAIL_TOKENS.ROW_MAX_WIDTH }}
              />
            )}
            <AddCircleButton
              label="Add contact"
              showLabel={false}
              onClick={() => addContactRef.current?.()}
            />
          </div>

          {/* Bottom padding */}
          <div style={{ height: '20px', flexShrink: 0 }} />
        </div>
      </ConnectionDetailShell>

      {/* Group selector overlay */}
      <AnimatePresence>
        {showGroupSelector && (
          <GroupSelector
            groups={groups}
            selectedGroupIds={getGroupsForConnection(sessionChatRoomId).map((g) => g.id)}
            onSave={async (groupIds) => {
              const viewId = await resolveConnectionViewId(sessionChatRoomId);
              if (viewId) groupActions.setConnectionGroups(viewId, groupIds);
            }}
            onClose={() => setShowGroupSelector(false)}
            onCreateGroup={groupActions.createCustomGroup}
            onDeleteGroup={groupActions.deleteCustomGroup}
          />
        )}
      </AnimatePresence>

      {/* Location overlay */}
      {showLocationOverlay && connectionData?.connectionMetadata && (
        <LocationOverlay
          nearbyVenues={
            connectionData.connectionMetadata?.locationData?.venueInfo?.nearbyVenues || []
          }
          currentVenue={connectionData.connectionMetadata?.locationData?.venueInfo?.venue || null}
          address={connectionData.connectionMetadata?.locationData?.venueInfo?.address || {}}
          coords={connectionData.connectionMetadata?.locationData?.coords || {}}
          showLocationContext={false}
          onVenueChange={(selectedVenue) => {
            handleVenueChange(selectedVenue);
            setShowLocationOverlay(false);
          }}
          onClose={() => setShowLocationOverlay(false)}
        />
      )}
    </>
  );
};

export default ManualAddDetailPage;
